import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import EventsList from './EventsList';
import "./Calendar.css";
import { ChevronLeft, ChevronRight, KeyboardArrowDown } from '@mui/icons-material';
import { Button, FormControl, ListItemText, MenuItem, Select } from '@mui/material';
import { getReleaseDetailsById } from '../../Services';
import { freezePeriods } from './consts';
import { eventTypes } from '../../utility/consts';

const localizer = momentLocalizer(moment);

function getImpactBars(relevance) {
  if (!relevance) return "-";
  const levels = { low: 1, medium: 2, high: 3 };
  return Array(3)
    .fill("#b6b6b6")
    .map((color, index) => (index < levels[String(relevance).toLowerCase()] ? "#454545" : color));
}

function CalendarHoverPopover({
  position,
  matchingEvents,
  currentPage,
  setCurrentPage,
  onMouseEnter,
  onMouseLeave,
  onViewMore,
}) {
  if (!position || !matchingEvents?.length) return null;

  const event = matchingEvents[currentPage];
  if (!event) return null;

  const bars = getImpactBars(event.relevance);
  const totalPages = matchingEvents.length;
  const scale = 0.78;

  const getVisiblePages = (currentPage, totalPages, maxSize = 4) => {
    if (totalPages <= 1) return [];

    const current = currentPage + 1;

    const startPage = Math.max(
      1,
      Math.min(current - Math.floor(maxSize / 2), totalPages - maxSize + 1)
    );

    const endPage = Math.min(totalPages, startPage + maxSize - 1);

    const pages = [];

    for (let i = startPage; i < endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages && current + 1 !== totalPages) {
      pages.push("ellipsis");
    }

    pages.push(totalPages);

    return pages;
  };

  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <div
      className="calendar-hover-popover"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        top: position.top,
        left: position.left,
        transform: `scale(${scale})`,
      }}
    >
      <div className="calendar-tooltip-container tooltip-container bg-white p-3 position-relative">
        <div
          className="calendar-tooltip-corner"
          style={{
            borderColor: `transparent ${event?.color?.hex || "#8b8cd6"} transparent transparent`,
          }}
        />

        <div className="tooltip-animate calendar-tooltip-content">
          <div className="tooltip-header pb-3 border-bottom calendar-tooltip-header">
            <span className="font-weight-bold">
              Source | {event?.Vendor_type || "N/A"}
            </span>

            <span
              className="d-block mt-2 text-body-1 font-weight-bold calendar-tooltip-title"
              title={event?.title || "N/A"}
            >
              <strong>{event?.title || "N/A"}</strong>
            </span>

            <span
              className="d-inline-block text-white text-xs badge font-weight-normal mt-2 calendar-tooltip-badge"
              style={{
                backgroundColor: event?.color?.hex || "#8b8cd6",
              }}
              title={event?.vendor_service || "N/A"}
            >
              {event?.vendor_service || "N/A"}
            </span>
          </div>

          <div className="tooltip-details">
            <div className="row d-flex justify-content-between p-0 m-0 pt-3 pb-1 px-1">
              <div className="col-4 font-weight-bold text-s text-body-color">
                <strong>RELEASE ID</strong>
              </div>
              <div className="col-4 pl-2 font-weight-bold text-s text-body-color">
                <strong>STATUS</strong>
              </div>
              <div className="col-4 font-weight-bold text-s text-body-color">
                <strong>LAST UPDATED</strong>
              </div>
            </div>

            <div className="row d-flex justify-content-between p-0 m-0 border-bottom px-1">
              <div
                className="col-4 pb-3 text-sm calendar-tooltip-ellipsis"
                title={event?.release_id || "N/A"}
              >
                {event?.release_id || "N/A"}
              </div>
              <div
                className="col-4 pb-3 pl-2 text-info text-sm calendar-tooltip-ellipsis"
                title={event?.release_status || "N/A"}
              >
                {event?.release_status || "N/A"}
              </div>
              <div className="col-4 pb-3 text-sm">
                {event?.last_updated ? moment(event.last_updated).format("MMM DD, YYYY") : "N/A"}
              </div>
            </div>

            <div className="row d-flex justify-content-between p-0 m-0 border-bottom px-1">
              <div className="col p-0 px-2 py-2">
                <div className="rounded p-1 d-block w-100 bg-gray text-sm">
                  <span>🕒 </span>
                  {moment(event?.planned_start_date).format("MMM DD, YYYY")} -{" "}
                  {moment(event?.planned_end_date).format("MMM DD, YYYY")}
                </div>
              </div>
            </div>

            <div className="row d-flex justify-content-between p-0 m-0 pt-3 pb-1 px-1">
              <div className="col-4 font-weight-bold text-s text-body-color">
                <strong>IMPACT</strong>
              </div>
              <div className="col-8 pl-2 font-weight-bold text-s text-body-color">
                <strong>CATEGORY</strong>
              </div>
            </div>

            <div className="row d-flex justify-content-between p-0 m-0 border-bottom px-1">
              <div className="col-4 pb-3 d-flex align-items-center">
                {typeof bars === "string"
                  ? bars
                  : bars.map((color, index) => (
                      <div
                        key={index}
                        className="impact-bar calendar-impact-bar"
                        style={{
                          backgroundColor: color,
                          marginLeft: index > 0 ? 5 : 0,
                        }}
                      />
                    ))}
              </div>
              <div
                className="col-8 pb-3 pl-2 text-sm calendar-tooltip-ellipsis"
                title={event?.release_category || "N/A"}
              >
                {event?.release_category || "N/A"}
              </div>
            </div>
          </div>

          <div className={`tooltip-footer mt-4 px-2 text-start text-body-sm ${totalPages === 1 ? "mb-2" : "mb-4"}`}>
            <a
              className="text-decoration-none text-blue view-more-button cursor-pointer"
              onClick={() => onViewMore(event)}
            >
              <strong>🡢 View More</strong>
            </a>
          </div>
        </div>

        {totalPages > 1 && (
          <nav aria-label="Page navigation" className="mt-2">
            <ul className="pagination justify-content-center pl-0 list-unstyled rounded mb-0">
              <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                <button
                  className="page-link border-0 px-3 py-1 bg-transparent text-primary text-body-sm"
                  onClick={() => currentPage > 0 && setCurrentPage(currentPage - 1)}
                >
                  &#8592;
                </button>
              </li>

              {visiblePages.map((page, index) => {
                if (page === "ellipsis") {
                  return (
                    <li key={`ellipsis-${index}`} className="page-item disabled">
                      <span className="page-link border-0 px-3 py-1 bg-transparent text-body-sm">
                        ...
                      </span>
                    </li>
                  );
                }

                return (
                  <li
                    key={page}
                    className={`page-item ${currentPage === page - 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link rounded-lg mx-2 py-1 border-0 calendar-tooltip-page-button"
                      onClick={() => setCurrentPage(page - 1)}
                    >
                      {page}
                    </button>
                  </li>
                );
              })}

              <li className={`page-item ${currentPage === totalPages - 1 ? "disabled" : ""}`}>
                <button
                  className="page-link border-0 px-3 py-1 bg-transparent text-primary text-body-sm"
                  onClick={() => currentPage < totalPages - 1 && setCurrentPage(currentPage + 1)}
                >
                  &#8594;
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
}

const CalendarView = ({ isDataLoaded, events, preference, releaseId, onClose }) => {
  const [selectedDay, setSelectedDay] = useState(moment());
  const [currentMonth, setCurrentMonth] = useState("");
  const [showLongReleases, setLongRelease] = useState(true);
  const [filtererdEvents, setFilteredEvents] = useState(events);
  const [groupedEvents, setGroupedEvents] = useState([]);
  const [selectedDateFilter, setSelectedDateFilter] = useState("Show All");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [hasFetchedRelease, setHasFetchedRelease] = useState(false);

  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [hoveredPosition, setHoveredPosition] = useState(null);
  const [hoveredPage, setHoveredPage] = useState(0);

  const hideTimerRef = useRef(null);

  const dateFilterList = [
    "Show All",
    "Next 3 months",
    "Next 6 months",
  ];

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const getVisiblePages = (currentPage, totalPages, maxSize = 4) => {
    if (totalPages <= 1) return [];

    const current = currentPage + 1; // convert to 1-based

    const startPage = Math.max(
      1,
      Math.min(current - Math.floor(maxSize / 2), totalPages - maxSize + 1)
    );

    const endPage = Math.min(totalPages, startPage + maxSize - 1);

    const pages = [];

    for (let i = startPage; i < endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages && current + 1 !== totalPages) {
      pages.push("ellipsis");
    }

    pages.push(totalPages);

    return pages;
  };

  const hidePopoverWithDelay = () => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setHoveredGroup(null);
      setHoveredPosition(null);
      setHoveredPage(0);
    }, 180);
  };

  const showPopover = (groupedEvent, targetElement) => {
    clearHideTimer();

    const rect = targetElement.getBoundingClientRect();
    const scale = 0.78;
    const tooltipWidth = 430 * scale;
    const tooltipHeightEstimate = 360 * scale;
    const gap = 12;

    let left = rect.right + gap;
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = rect.left - tooltipWidth - gap;
    }
    if (left < 16) left = 16;

    let top = rect.top;
    if (top + tooltipHeightEstimate > window.innerHeight - 50) {
      top = window.innerHeight - tooltipHeightEstimate - 50;
    }
    if (top < 16) top = 16;

    setHoveredGroup(groupedEvent);
    setHoveredPosition({ top, left });
    setHoveredPage(0);
  };

  useEffect(() => {
    if (releaseId && window.location.pathname !== "/") {
      const event = events.find(e => e.release_id === releaseId);

      if (event?.release_id && event?.vendor_service) {
        setSelectedEvent(event);
      } else if (!hasFetchedRelease) {
        setHasFetchedRelease(true);
        getReleaseDetailsById(releaseId)
          .then(({ data }) => data?.release_id && data?.vendor_service && setSelectedEvent(data))
          .catch(error => console.error("Error fetching event details:", error));
      }
    }
  }, [releaseId, events, hasFetchedRelease]);

  useEffect(() => {
    const calendar = document.querySelector('.rbc-month-view');

    const handleClick = (e) => {
      if (e.target.classList.contains('change-freeze-day') || e.target.classList.contains('change-freeze-dot')) {
        window.open(
          'https://share.autodesk.com/sites/IT_EAISG/rm2/SitePages/Change-Freeze-Calendar.aspx',
          '_blank',
          'noopener noreferrer'
        );
      }
    };

    if (calendar) {
      calendar.addEventListener('click', handleClick);
    }

    return () => {
      if (calendar) {
        calendar.removeEventListener('click', handleClick);
      }
    };
  }, []);

  useEffect(() => {
    setCurrentMonth(moment(selectedDay).format("MMMM YYYY"));
  }, [selectedDay]);

  useEffect(() => {
    const groupedRecords = filtererdEvents.reduce((result, event) => {
      const vendorType = event.Vendor_type;
      const startDate = moment(event.planned_start_date).startOf('day');
      const endDate = moment(event.planned_end_date).startOf('day');

      for (
        let date = moment(eventTypes.includes(event.Vendor_type) ? endDate : startDate);
        date.isSameOrBefore(endDate);
        date.add(1, 'day')
      ) {
        const dayDate = date.clone().tz(moment.tz.guess()).startOf('day').toDate();
        const key = `${vendorType}_${moment(dayDate).format("YYYY-MM-DD")}`;

        if (!result[key]) {
          result[key] = {
            title: `${vendorType} (1)`,
            Vendor_type: vendorType,
            planned_start_date: dayDate,
            planned_end_date: dayDate,
            color: event.color,
            count: 1,
            matchingEvents: [event],
          };
        } else {
          result[key].count += 1;
          result[key].title = `${vendorType} (${result[key].count})`;
          result[key].matchingEvents.push(event);
        }
      }

      return result;
    }, {});

    const finalGroupedEvents = Object.values(groupedRecords).map(group => ({
      ...group,
      matchingEvents: group.matchingEvents.sort((a, b) => {
        const dateA = moment(eventTypes.includes(a.Vendor_type) ? a.planned_end_date : a.planned_start_date).valueOf();
        const dateB = moment(eventTypes.includes(b.Vendor_type) ? b.planned_end_date : b.planned_start_date).valueOf();
        return dateA - dateB;
      }),
    }));

    setGroupedEvents(finalGroupedEvents);
  }, [filtererdEvents]);

  useEffect(() => {
    let filterList = events;
    if (selectedDateFilter !== "Show All") {
      filterList = filterByDate(filterList);
    }
    if (!showLongReleases) {
      filterList = filterLongReleases(filterList);
    }
    setFilteredEvents(filterList);
  }, [showLongReleases, events, selectedDateFilter]);

  useEffect(() => {
    return () => clearHideTimer();
  }, []);

  const handleEventSelection = async (event) => {
    let date = event?.planned_start_date;
    if (date) {
      const mDate = moment(date);
      if (mDate.isValid()) {
        setSelectedDay(mDate);
      } else {
        console.error("Invalid date:", date);
      }
    } else {
      console.error("Missing or undefined date:", date);
    }
  };

  const filterEventsByDate = (event) => {
    const startDate = moment(eventTypes.includes(event.Vendor_type) ? event.planned_end_date : event.planned_start_date).startOf('day');
    const endDate = moment(event.planned_end_date).startOf('day');
    const selected = moment(selectedDay).startOf('day');
    return startDate.isSameOrBefore(selected) && endDate.isSameOrAfter(selected);
  };

  const toggleMonth = (isNext) => {
    const newDate = moment(selectedDay).add(isNext ? 1 : -1, 'month');
    setSelectedDay(newDate);
  };

  const filterLongReleases = (data) => {
    let long_duration = 7;
    if (preference?.["long_release"] === "2 weeks") long_duration = 14;
    else if (preference?.["long_release"] === "4 weeks") long_duration = 28;

    return data.filter((event) => {
      const start = moment(event.planned_start_date);
      const end = moment(event.planned_end_date);
      return end.diff(start, 'days') <= long_duration;
    });
  };

  const handleDateChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedDateFilter(value);
  };

  const filterByDate = (filterList) => {
    const dateIndex = dateFilterList.indexOf(selectedDateFilter);
    let newDate = moment();
    const today = moment();

    if (dateIndex === 1) {
      newDate = newDate.add(3, 'months');
    } else if (dateIndex === 2) {
      newDate = newDate.add(6, 'months');
    }

    return filterList.filter((event) => compareDate(event, newDate, today));
  };

  const compareDate = (event, newDate, today) => {
    const startDate = moment(event.planned_start_date).startOf('day');
    const endDate = moment(event.planned_end_date).startOf('day');

    return today.isAfter(newDate)
      ? startDate.isSameOrBefore(today) && endDate.isSameOrAfter(newDate)
      : startDate.isSameOrBefore(newDate) && endDate.isSameOrAfter(today);
  };

  const checkFreezePeriod = (freezePeriods, selectedDay) => {
    return freezePeriods.some(({ start, end }) =>
      [start, end].some(d =>
        moment(d).month() === moment(selectedDay).month() && moment(d).year() === moment(selectedDay).year()
      )
    );
  };

  const dayPropGetter = (date) => {
    const currentDate = moment(date).startOf('day');
    const selectedDayDate = moment(selectedDay).startOf('day');

    const isInFreezePeriod = freezePeriods.some(period =>
      currentDate.isSameOrAfter(moment(period.start).startOf('day')) &&
      currentDate.isSameOrBefore(moment(period.end).startOf('day'))
    );

    const baseStyle = {
      ...(currentDate.isSame(selectedDayDate) && {
        backgroundColor: '#00000009',
        border: '1px solid #111',
      }),
      ...(isInFreezePeriod && { position: 'relative' })
    };

    return {
      style: baseStyle,
      className: isInFreezePeriod ? 'change-freeze-day change-freeze-dot' : undefined,
    };
  };

  const isFreezeMonth = useCallback(
    () => checkFreezePeriod(freezePeriods, selectedDay),
    [selectedDay]
  );

  const handleViewMore = (event) => {
    setSelectedEvent(event);
    setSelectedDay(moment(event.planned_start_date));
    setHoveredGroup(null);
    setHoveredPosition(null);
    setHoveredPage(0);
  };
  const CalendarEvent = ({ event }) => (
    <div
      onMouseEnter={(e) => showPopover(event, e.currentTarget)}
      onMouseLeave={hidePopoverWithDelay}
      className="calendar-event-hover-trigger"
      title=""
    >
      <span>{event.title}</span>
    </div>
  );

  return (
    <div className="row mt-4 position-relative">
      <div className="col-8 pr-0">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <h5 className='font-weight-bold text-24 mr-2'>{currentMonth}</h5>
            {isFreezeMonth() && (
              <span className="d-flex mr-2 text-lite mb-1">
                {"("}
                <span className="change-freeze-dot d-inline-flex align-items-center mr-2 ml-1"></span>
                <span className="text-lite d-inline-flex align-items-center mr-1">
                  Planned Change Freeze
                </span>
                {")"}
              </span>
            )}
          </div>

          <div className="d-flex align-items-center">
            <FormControl className="source-list ml-2" size="small">
              <Button
                variant="contained"
                disableElevation
                className="mr-1"
                size="small"
                endIcon={<KeyboardArrowDown />}
              >
                {selectedDateFilter}
              </Button>
              <Select
                labelId="source"
                id="source"
                value={selectedDateFilter}
                onChange={handleDateChange}
                sx={{ width: "100%" }}
              >
                {dateFilterList.map((date) => (
                  <MenuItem key={date} value={date}>
                    <ListItemText primary={date} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <button
              className="btn btn-sm btn-outline-primary mr-2 ml-2"
              onClick={() => {
                setSelectedDay(moment());
              }}
            >
              Today
            </button>

            <div
              className="btn-group btn-group-sm"
              role="group"
              aria-label="Basic example"
            >
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  toggleMonth(false);
                }}
                disabled={moment(selectedDay).isBefore(moment(), 'day')}
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  toggleMonth(true);
                }}
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>

        <Calendar
          className="calendar-container"
          localizer={localizer}
          views={["month"]}
          events={groupedEvents}
          popup
          date={selectedDay}
          selectable
          components={{
            event: CalendarEvent,
          }}
          startAccessor={(event) => moment(event.planned_start_date).toDate()}
          endAccessor={(event) => moment(event.planned_end_date).toDate()}
          dayPropGetter={dayPropGetter}
          eventPropGetter={(myEventsList) => {
            const bg = myEventsList.color
              ? myEventsList.color["hex"] + '30'
              : "#8b8cd630";
            const color = myEventsList.color
              ? myEventsList.color["hex"]
              : "#8b8cd6";
            return {
              style: {
                backgroundColor: bg,
                color: color,
                borderColor: color,
              }
            };
          }}
          onSelectEvent={handleEventSelection}
          onNavigate={(date) => {
            setSelectedDay(date);
          }}
        />
      </div>

      <div className="col-4">
        {selectedDay && (
          <EventsList
            isDataLoaded={isDataLoaded}
            hasFetchedRelease={hasFetchedRelease}
            selectedDay={selectedDay}
            events={filtererdEvents.filter(filterEventsByDate)}
            event={selectedEvent}
            onClose={onClose}
          />
        )}
      </div>

      <CalendarHoverPopover
        position={hoveredPosition}
        matchingEvents={hoveredGroup?.matchingEvents || []}
        currentPage={hoveredPage}
        setCurrentPage={setHoveredPage}
        onMouseEnter={clearHideTimer}
        onMouseLeave={hidePopoverWithDelay}
        onViewMore={handleViewMore}
      />
    </div>
  );
};

export default CalendarView;